// src/App.jsx - Router principal v4.0 (circuit fermé)
import { useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore, useMessageStore } from './store';
import { useNotifications }  from './hooks/useNotifications';
import NotificationToast     from './components/NotificationToast';
import ErrorBoundary         from './components/ErrorBoundary';

const LoginPage    = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.RegisterPage })));
const ChatPage     = lazy(() => import('./pages/ChatPage').then(m => ({ default: m.ChatPage })));
const WalletPage   = lazy(() => import('./pages/WalletPage').then(m => ({ default: m.WalletPage })));
const WalletRechargePage = lazy(() => import('./pages/DiasporaRechargePage').then(m => ({ default: m.DiasporaRechargePage })));
const AdminPayoutPage = lazy(() => import('./pages/AdminPayoutPage').then(m => ({ default: m.AdminPayoutPage })));
const MerchantDashboard = lazy(() => import('./pages/MerchantDashboard'));
const PricingPage  = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const PrivacyPage  = lazy(() => import('./pages/PrivacyPage'));

const RouteLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1420', color: '#6b7fa3', fontFamily: "'Sora', sans-serif" }}>
    Chargement...
  </div>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? children : <Navigate to="/" replace />;
};

function AppInner() {
  const { initialize, isAuthenticated, isInitialized } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => { initialize(); }, []);

  const handleOpenConversation = useCallback((convId) => {
    useMessageStore.getState().setActiveConversation(convId);
    navigate('/');
  }, [navigate]);

  const { inAppNotifs, dismissNotif } = useNotifications({
    onOpenConversation: isAuthenticated ? handleOpenConversation : undefined,
  });

  if (!isInitialized) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1420', color: '#6b7fa3', fontFamily: "'Sora', sans-serif" }}>
        Chargement...
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" toastOptions={{
        style: { background:'#1a2235', color:'#E8EDF5', border:'1px solid #1e2d45', fontFamily:"'Sora', sans-serif", fontSize:14 },
        success: { iconTheme: { primary:'#00D4AA', secondary:'#000' } },
        error:   { iconTheme: { primary:'#FF4F6A', secondary:'#fff' } },
      }} />
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/pricing"  element={<PricingPage />} />
          <Route path="/privacy"  element={<PrivacyPage onClose={()=>window.history.back()} />} />
          <Route path="/"         element={<PrivateRoute><ChatPage /></PrivateRoute>} />
          <Route path="/wallet"   element={<PrivateRoute><WalletPage /></PrivateRoute>} />
          <Route path="/wallet/recharge" element={<PrivateRoute><WalletRechargePage /></PrivateRoute>} />
          <Route path="/admin/revenus"   element={<PrivateRoute><AdminPayoutPage /></PrivateRoute>} />
          <Route path="/merchant"        element={<PrivateRoute><MerchantDashboard /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* In-app notification toasts — rendered outside Routes so always visible */}
      <NotificationToast
        notifications={inAppNotifs}
        onDismiss={dismissNotif}
        onOpen={(notif) => notif.conversationId && handleOpenConversation(notif.conversationId)}
      />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
